import React, { useCallback, useState } from 'react';
import { t } from '@lingui/macro';
import styled, { css } from 'styled-components';

import { Close } from '@rover/icons';
import { Box, Button } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import { DSTokenMap, ZIndex } from '@rover/kibble/styles';
import { BackgroundColors } from '@rover/kibble/tokens/build/rover/es6/theme';

type Props = {
  children: React.ReactNode;
  canDismiss?: boolean;
  onDismissClick?: () => void;
  background?: BackgroundColors;
  className?: string;
};

type ScrollBoundaryProps = {
  offsetTop?: string | number;
};

// declaring styled components so they can be referenced in css
const FloatingFooterContainer = styled(Box)``;
const StyledClose = styled(Close)``;

const StyledButton = styled(Button)`
  // this has to be nested to override default Button styles
  ${StyledClose} {
    cursor: pointer;
    fill: ${DSTokenMap.INTERACTIVE_TEXT_COLOR_PRIMARY.toString()};
  }
  &:hover,
  &:focus {
    background-color: ${DSTokenMap.BACKGROUND_COLOR_TERTIARY.toString()};
  }
`;

const FloatingFooter = ({
  children,
  canDismiss = false,
  onDismissClick,
  background = 'secondary',
  className,
}: Props): JSX.Element => {
  const [isSticky, setIsSticky] = useState(true);

  const onDismiss = useCallback(() => {
    onDismissClick?.();
    setIsSticky(false);
  }, [onDismissClick]);

  return (
    <FloatingFooterContainer
      py="4x"
      position={isSticky ? 'sticky' : 'static'}
      bottom="0"
      width="100%"
      borderTop={`1px solid ${DSTokenMap.BORDER_COLOR_SECONDARY.toString()}`}
      background={background}
      sx={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)' }}
      zIndex={ZIndex.NAVBAR_FIXED.toString()}
      className={className}
    >
      <Section>
        {canDismiss ? (
          <Box px="6x">
            {isSticky && (
              <Box position="absolute" top="3x" right={['1x', '1x', '3x']}>
                <StyledButton
                  onClick={onDismiss}
                  size="small"
                  variant="flat"
                  aria-label={t`Dismiss`}
                  circular
                  icon={StyledClose}
                  data-testid="dismiss-button"
                />
              </Box>
            )}
            {children}
          </Box>
        ) : (
          children
        )}
      </Section>
    </FloatingFooterContainer>
  );
};

// eslint-disable-next-line rover/prefer-kibble-components
FloatingFooter.ScrollBoundary = styled.div<ScrollBoundaryProps>`
  position: relative;

  /* This targets the last element inside the scroll boundary before the floating footer.
   * The negative margin needs to be applied, otherwise there will be an $offsetTop-sized 
   * space between the content and the footer when scrolled to the bottom. 
   * This will override any margin-bottom on that element (usually a PageLayout). 
   * If that margin is necessary, wrap it in a container, or add the margin to the last 
   * child of that element. */
  & > :nth-last-child(2) {
    margin-bottom: ${(props) => css`calc(${props.offsetTop} * -1) !important`};
  }

  & > ${FloatingFooterContainer}:last-child {
    margin-top: ${(props) => props.offsetTop};
  }
`;

FloatingFooter.ScrollBoundary.defaultProps = {
  offsetTop: 0,
};

export default FloatingFooter;
